import { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  Stack,
  Card,
  CardContent,
  Alert,
  Link,
  Container,
} from '@mui/material';
import GoogleIcon from '@mui/icons-material/Google';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import { initiateOAuth } from '../api/auth';
import { saveOAuthState } from '../services/tokenStorage';

export default function LoginPage() {
  const [loading, setLoading] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const handleLogin = async (provider: 'google') => {
    try {
      setLoading(provider);
      setError(null);
      const { authUrl, state } = await initiateOAuth(provider);
      saveOAuthState(state);
      window.location.href = authUrl;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      setLoading(null);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        flexDirection: 'column',
        bgcolor: 'background.default',
        background: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
      }}
    >
      <Container maxWidth="sm" sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', py: 4 }}>
        <Box sx={{ mb: 4, textAlign: 'center' }}>
          <Box
            sx={{
              display: 'inline-flex',
              p: 1.5,
              borderRadius: 3,
              bgcolor: 'primary.main',
              color: 'white',
              mb: 2,
              boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.3)',
            }}
          >
            <ReceiptLongIcon fontSize="large" />
          </Box>
          <Typography
            variant="h4"
            component="h1"
            gutterBottom
            fontWeight="700"
            color="text.primary"
          >
            subtotal.io
          </Typography>
          <Typography
            variant="body1"
            color="text.secondary"
            sx={{ maxWidth: 400, mx: 'auto' }}
          >
            Intelligent receipt processing - maximize your purchasing power
          </Typography>
        </Box>

        <Card sx={{ maxWidth: 400, width: '100%', mx: 'auto', mb: 4 }}>
          <CardContent sx={{ p: 4 }}>
            <Typography variant="h6" gutterBottom align="center" sx={{ mb: 3 }}>
              Welcome back
            </Typography>

            {error && (
              <Alert severity="error" sx={{ mb: 3 }} onClose={() => setError(null)}>
                {error}
              </Alert>
            )}

            <Stack spacing={2}>
              <Button
                variant="outlined"
                size="large"
                fullWidth
                startIcon={<GoogleIcon />}
                onClick={() => handleLogin('google')}
                disabled={loading !== null}
                sx={{
                  color: 'text.primary',
                  borderColor: '#e5e7eb',
                  bgcolor: 'white',
                  '&:hover': {
                    borderColor: '#d1d5db',
                    bgcolor: '#f9fafb',
                  },
                }}
              >
                {loading === 'google' ? 'Signing in...' : 'Sign in with Google'}
              </Button>

            </Stack>
          </CardContent>
        </Card>

        <Box component="footer" textAlign="center">
          <Typography variant="body2" color="text.secondary">
            &copy; {new Date().getFullYear()} subtotal.io. All rights reserved.
          </Typography>
          <Stack direction="row" spacing={2} justifyContent="center" sx={{ mt: 1 }}>
            <Link href="https://subtotal.io/privacy.html" color="text.secondary" underline="hover" variant="body2">
              Privacy Policy
            </Link>
            <Link href="https://subtotal.io/terms.html" color="text.secondary" underline="hover" variant="body2">
              Terms of Service
            </Link>
          </Stack>
        </Box>
      </Container>
    </Box>
  );
}
